export function hexToHsl(hex) {
  if (!hex) return null;
  let h = hex.replace("#", "").trim();
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return {
    h: Math.round(hue * 360),
    s: Math.round(sat * 100),
    l: Math.round(l * 100),
  };
}

// String form kept for backward compatibility with existing callers that
// expected the old shape `"H S% L%"`.
export function hexToHslString(hex) {
  const hsl = hexToHsl(hex);
  return hsl ? `${hsl.h} ${hsl.s}% ${hsl.l}%` : null;
}

/**
 * Apply the brand color to the CSS custom properties consumed by the
 * shadcn/ui components.
 *
 * Also computes a **brightened** variant (`--primary-dark`) so that the
 * `.dark` theme can boost the brand color for readability against the
 * near-black dark-mode background. Without this, mid-lightness brand
 * colors (e.g. `#5dbf0d`) become too muted to see when used as the
 * active-nav underline / text-primary highlight.
 */
export function applyBrandColor(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return;
  const { h, s, l } = hsl;
  const root = document.documentElement;
  const lightVar = `${h} ${s}% ${l}%`;
  // Dark-mode variant. Iter 107b — Keith flagged the previous boost
  // (L+20 clamped 55–70, S+10) as "neon" against the near-black
  // background — solid buttons stopped being readable because the
  // white foreground melted into the bright green. Tone it down:
  //   • Target lightness in the 42–55 band (never brighter than the
  //     original if it was already saturated),
  //   • Slightly *drop* saturation for very saturated brands so the
  //     surface reads as a solid button, not a highlighter marker.
  const boostedL = Math.max(42, Math.min(55, l));
  const boostedS = s > 70 ? Math.max(55, s - 15) : s;
  const darkVar  = `${h} ${boostedS}% ${boostedL}%`;
  // Ring highlight still benefits from a lighter tone so focus rings
  // stay visible on dark cards — keep the old brighten just for ring.
  const ringDarkL = Math.min(70, Math.max(l + 15, 55));
  const ringDarkVar = `${h} ${Math.min(100, s)}% ${ringDarkL}%`;
  root.style.setProperty("--primary-light", lightVar);
  root.style.setProperty("--primary-dark", darkVar);
  root.style.setProperty("--ring-light", lightVar);
  root.style.setProperty("--ring-dark", ringDarkVar);
}
